/** @typedef {'LOAN_IN' | 'LOAN_OUT'} LoanProfileType */

const LOAN_RETURN_PURPOSES = new Set(['LOAN_IN_RETURN', 'LOAN_OUT_RETURN']);

/**
 * True for approved/pending loan-in *borrow* rows — excludes return-via-receiving paperwork.
 * @param {object} record
 */
export function isLoanInBorrowReceiving(record) {
  if (!record || String(record.loanDirection || '').trim().toUpperCase() !== 'LOAN_IN') {
    return false;
  }
  const purpose = String(record.loanReceivingPurpose || 'LOAN_IN').trim().toUpperCase();
  if (LOAN_RETURN_PURPOSES.has(purpose)) return false;
  if (record.linkedSourceRecordId) return false;
  return true;
}

/**
 * @param {string} value
 */
export function toDateInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * @param {object} record
 * @param {LoanProfileType} type
 */
export function loanRecordToProfile(record, type) {
  if (!record?._id) return null;
  const counterpartyCompany = String(record.counterpartyCompany || '').trim();
  const loanReferenceNumber = String(record.loanReferenceNumber || '').trim();
  const supplier = record.supplier?._id || record.supplier || '';
  return {
    id: `${type}:${record._id}`,
    type,
    recordId: String(record._id),
    counterpartyCompany,
    loanReferenceNumber,
    counterpartyContactName: String(record.counterpartyContactName || '').trim(),
    counterpartyContactPhone: String(record.counterpartyContactPhone || '').trim(),
    returnDueDate: toDateInputValue(record.returnDueDate),
    returnStatus: record.returnStatus || record.status || '',
    supplier: supplier ? String(supplier) : ''
  };
}

/**
 * @param {Array} loanInRecords
 * @param {Array} loanOutRecords
 */
/**
 * Build receiving grid lines from a loan-in or loan-out record.
 * @param {object} record
 * @param {LoanProfileType} profileType
 * @param {string} [purpose] LOAN_IN | LOAN_IN_RETURN | LOAN_OUT_RETURN
 */
/**
 * Carry aircraft tagging from a source receiving/issuance line into a new receiving line.
 */
function aircraftFieldsFromSourceLine(line) {
  const scope = String(line?.aircraftScope || '').toUpperCase() === 'AIRCRAFT' ? 'AIRCRAFT' : 'GEN';
  const aircraftId = line?.aircraftId?._id ?? line?.aircraftId;
  if (scope !== 'AIRCRAFT' || !aircraftId) {
    return {
      aircraftScope: 'GEN',
      aircraftId: undefined,
      aircraftRegistration: 'GEN'
    };
  }
  return {
    aircraftScope: 'AIRCRAFT',
    aircraftId: String(aircraftId),
    aircraftRegistration: String(line?.aircraftRegistration || line?.aircraftId?.registration || '').toUpperCase() || 'GEN'
  };
}

export function buildReceivingItemsForLoanRecord(record, profileType, purpose = 'LOAN_IN') {
  const p = String(purpose || 'LOAN_IN').trim().toUpperCase();
  if (p === 'LOAN_IN_RETURN' && profileType === 'LOAN_IN') {
    return buildOutstandingReturnLines(record, 'LOAN_IN_RETURN');
  }
  if (p === 'LOAN_OUT_RETURN' && profileType === 'LOAN_OUT') {
    return buildOutstandingReturnLines(record, 'LOAN_OUT_RETURN');
  }
  if (profileType === 'LOAN_IN') {
    return (Array.isArray(record?.items) ? record.items : [])
      .map((line) => {
        const baseQty = Number(line.receivedQuantity || line.quantity || 0);
        return {
          partNumber: line.partNumber || '',
          description: line.description || line.partNumber || '',
          quantity: Math.max(baseQty, 1),
          receivedQuantity: 0,
          condition: line.condition || 'SERVICEABLE',
          storeType: line.storeType || '',
          locationText: line.locationText || '',
          certificateNumber: line.certificateNumber || '',
          batchNumber: line.batchNumber || '',
          serialNumber: line.serialNumber || '',
          shelfLifeUpto: line.shelfLifeNotApplicable
            ? ''
            : (line.shelfLifeUpto ? toDateInputValue(line.shelfLifeUpto) : ''),
          shelfLifeNotApplicable: Boolean(line.shelfLifeNotApplicable),
          remarks: line.remarks || '',
          unitDetails: Array.isArray(line.unitDetails)
            ? line.unitDetails.map((unit) => (
              line.shelfLifeNotApplicable ? { ...unit, expiryDate: '' } : unit
            ))
            : [],
          ...aircraftFieldsFromSourceLine(line)
        };
      })
      .filter((line) => String(line.partNumber || '').trim());
  }
  return [];
}

/**
 * Pick the best loan record for a reference (most recent first).
 */
export function pickLoanRecordForReference(loanInRecords, loanOutRecords, loanRef, counterparty, purpose = 'LOAN_IN') {
  const ref = String(loanRef || '').trim();
  const company = String(counterparty || '').trim();
  if (!ref) return null;

  const matches = (records, type) => records
    .filter((rec) => {
      const recRef = String(rec.loanReferenceNumber || '').trim();
      const recCo = String(rec.counterpartyCompany || '').trim();
      return recRef === ref && (!company || recCo === company);
    })
    .map((rec) => ({ record: rec, type }))
    .sort((a, b) => {
      const da = new Date(a.record.receivingDate || a.record.createdAt || 0).getTime();
      const db = new Date(b.record.receivingDate || b.record.createdAt || 0).getTime();
      return db - da;
    });

  const p = String(purpose || '').trim().toUpperCase();
  if (p === 'LOAN_IN_RETURN') {
    return matches(loanInRecords, 'LOAN_IN')[0] || null;
  }
  if (p === 'LOAN_OUT_RETURN') {
    return matches(loanOutRecords, 'LOAN_OUT')[0] || null;
  }

  return matches(loanInRecords, 'LOAN_IN')[0] || matches(loanOutRecords, 'LOAN_OUT')[0] || null;
}

export function buildLoanReceivingProfiles(loanInRecords = [], loanOutRecords = []) {
  const profiles = [];
  for (const rec of loanInRecords) {
    const p = loanRecordToProfile(rec, 'LOAN_IN');
    if (p && (p.counterpartyCompany || p.loanReferenceNumber)) profiles.push(p);
  }
  for (const rec of loanOutRecords) {
    const p = loanRecordToProfile(rec, 'LOAN_OUT');
    if (p && (p.counterpartyCompany || p.loanReferenceNumber)) profiles.push(p);
  }
  const counterparties = [...new Set(profiles.map((p) => p.counterpartyCompany).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b)
  );
  return { profiles, counterparties };
}

/**
 * @param {object} record
 * @param {'LOAN_IN_RETURN' | 'LOAN_OUT_RETURN'} purpose
 */
export function buildOutstandingReturnLines(record, purpose) {
  const lines = Array.isArray(record?.items) ? record.items : [];
  return lines
    .map((line, lineIndex) => {
      const orderedQty = Number(line.quantity || line.receivedQuantity || 0);
      const receivedQty = purpose === 'LOAN_IN_RETURN'
        ? Number(line.receivedQuantity || line.quantity || 0)
        : orderedQty;
      const returnedQty = Number(line.quantityReturned || 0);
      const outstandingQty = Math.max(0, receivedQty - returnedQty);
      if (outstandingQty <= 0) return null;
      return {
        lineIndex,
        partNumber: line.partNumber || '',
        description: line.description || line.partNumber || '',
        quantity: outstandingQty,
        receivedQuantity: outstandingQty,
        sourceLineIndex: lineIndex,
        condition: line.condition || 'SERVICEABLE',
        storeType: line.storeType || '',
        locationText: line.locationText || '',
        certificateNumber: line.certificateNumber || '',
        batchNumber: line.batchNumber || '',
        serialNumber: line.serialNumber || '',
        shelfLifeUpto: line.shelfLifeNotApplicable
          ? ''
          : (line.shelfLifeUpto || ''),
        shelfLifeNotApplicable: Boolean(line.shelfLifeNotApplicable),
        remarks: line.remarks || '',
        unitDetails: Array.isArray(line.unitDetails) ? line.unitDetails : [],
        ...aircraftFieldsFromSourceLine(line)
      };
    })
    .filter(Boolean);
}

/**
 * @param {object} line
 * @param {'LOAN_IN_RETURN' | 'LOAN_OUT_RETURN'} purpose
 */
export function outstandingQtyForReturnLine(line, purpose) {
  if (purpose === 'LOAN_IN_RETURN') {
    const receivedQty = Number(line?.receivedQuantity || line?.quantity || 0);
    const returnedQty = Number(line?.quantityReturned || 0);
    return Math.max(0, receivedQty - returnedQty);
  }
  const issuedQty = Number(line?.quantity || 0);
  const returnedQty = Number(line?.quantityReturned || 0);
  return Math.max(0, issuedQty - returnedQty);
}

/**
 * @param {number | string | null | undefined} lineIndex
 * @param {Array} sourceItems
 * @param {string} partNumber
 */
export function resolveReturnSourceLineIndexClient(lineIndex, sourceItems, partNumber) {
  if (lineIndex != null && Number.isFinite(Number(lineIndex))) {
    return Number(lineIndex);
  }
  const partKey = String(partNumber || '').trim().toLowerCase();
  if (!partKey) return null;
  const matches = (sourceItems || [])
    .map((src, idx) => ({ src, idx }))
    .filter(({ src }) => String(src.partNumber || '').trim().toLowerCase() === partKey);
  return matches.length === 1 ? matches[0].idx : null;
}

/**
 * Validate return lines against a linked loan-in or loan-out source.
 * @param {Array} items Receiving form lines
 * @param {Array} sourceItems Lines from linked source record
 * @param {'LOAN_IN_RETURN' | 'LOAN_OUT_RETURN'} purpose
 * @returns {string | null} Error message or null if valid
 */
export function validateLoanReturnItemsForSubmit(items, sourceItems, purpose) {
  const activeLines = (items || []).filter((line) => Number(line.receivedQuantity) > 0);
  if (!activeLines.length) {
    return 'At least one return line must have quantity greater than zero.';
  }

  for (const line of activeLines) {
    const qty = Number(line.receivedQuantity) || 0;
    const lineIndex = resolveReturnSourceLineIndexClient(line.sourceLineIndex, sourceItems, line.partNumber);
    if (lineIndex == null) {
      return `Return line for ${line.partNumber || 'part'} must map to the original loan line.`;
    }
    const entry = sourceItems[lineIndex];
    if (!entry) {
      return `Invalid source line mapping for ${line.partNumber || 'part'}.`;
    }
    const remaining = outstandingQtyForReturnLine(entry, purpose);
    if (qty > remaining) {
      return `Return quantity for ${line.partNumber} exceeds outstanding (${remaining} left).`;
    }
  }

  return null;
}

/**
 * Resolve supplier agency id for loan-out return receiving (from matching approved loan-in).
 * @param {object} loanOutRecord
 * @param {Array} loanInRecords
 */
export function resolveSupplierForLoanOutReturn(loanOutRecord, loanInRecords = []) {
  const ref = String(loanOutRecord?.loanReferenceNumber || '').trim();
  const company = String(loanOutRecord?.counterpartyCompany || '').trim();
  if (!ref && !company) return '';
  const match = loanInRecords.find((rec) => {
    const recRef = String(rec.loanReferenceNumber || '').trim();
    const recCo = String(rec.counterpartyCompany || '').trim();
    return rec.workflowStatus === 'APPROVED'
      && (!ref || recRef === ref)
      && (!company || recCo === company);
  });
  if (!match) return '';
  return match.supplier?._id || match.supplier || '';
}

export function buildLoanReturnNavigateState(record, purpose, options = {}) {
  const { loanInRecords = [] } = options;
  const profile = loanRecordToProfile(
    record,
    purpose === 'LOAN_IN_RETURN' ? 'LOAN_IN' : 'LOAN_OUT'
  );
  const items = buildOutstandingReturnLines(record, purpose);
  let supplier = profile?.supplier || '';
  if (purpose === 'LOAN_OUT_RETURN' && !supplier) {
    supplier = resolveSupplierForLoanOutReturn(record, loanInRecords);
  }
  return {
    receivingType: 'LOAN_RENTAL',
    loanReceivingPrefill: {
      loanReceivingPurpose: purpose,
      linkedSourceRecordId: String(record._id),
      supplier,
      counterpartyCompany: profile?.counterpartyCompany || '',
      counterpartyContactName: profile?.counterpartyContactName || '',
      counterpartyContactPhone: profile?.counterpartyContactPhone || '',
      loanReferenceNumber: profile?.loanReferenceNumber || '',
      returnDueDate: profile?.returnDueDate || '',
      lockMeta: true,
      items
    }
  };
}
